/**
 * ListingsManager — admin tab for browsing listings + exporting MLS data.
 *
 * Toolbar (search by address/#, status filter), selectable table or mobile
 * card list, and an "Export to MLS ▾" dropdown with JSON / CSV options.
 * Post-download warnings (missing-required-field rows) surface in a toast.
 *
 * Wires to `APIAgent.getHouses` for the list and the new
 * `APIAgent.exportMls(listingIds, format)` for the download.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import APIAgent from '../../agents/apiAgent';
import './ListingsManager.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'sold', label: 'Sold' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const PAGE_SIZE = 50;

const formatRelativeAge = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const statusClass = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'listings-manager__badge--success';
    case 'pending':
      return 'listings-manager__badge--warning';
    case 'draft':
      return 'listings-manager__badge--neutral';
    case 'sold':
    case 'withdrawn':
      return 'listings-manager__badge--neutral-strike';
    default:
      return 'listings-manager__badge--neutral';
  }
};

export default function ListingsManager() {
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [toast, setToast] = useState(null);

  const menuRef = useRef(null);
  const exportButtonRef = useRef(null);

  // Fetch all listings — admin sees everything via the existing /api/houses
  // endpoint (no admin-specific list exists in this slice; backend dev may
  // add one and we'll swap the call in.)
  //
  // QA T-29 caveat: /api/houses now filters to buyer-visible statuses
  // (active / pending / sold) for everyone EXCEPT an authenticated admin, and
  // "admin" there means an email listed in the backend's ADMIN_EMAILS setting
  // (see backend/app/services/listing_visibility.py:is_admin_viewer — the
  // registration allowlist deliberately does NOT count). If ADMIN_EMAILS is
  // unset for an environment, this table degrades to buyer-visible listings
  // and drafts/withdrawn rows go missing; configure ADMIN_EMAILS to restore it.
  const fetchHouses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await APIAgent.getHouses(null, PAGE_SIZE, true);
      const list = Array.isArray(result?.houses) ? result.houses : [];
      setHouses(list);
    } catch (err) {
      console.error('Listings fetch failed:', err);
      setError(err.message || 'Could not load listings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHouses();
  }, [fetchHouses]);

  // Close the export menu on outside click + escape key.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClickAway = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        exportButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return houses.filter((h) => {
      if (statusFilter && h.listing_status !== statusFilter) return false;
      if (!term) return true;
      const addr = (h.address || '').toLowerCase();
      const num = h.listing_number != null ? String(h.listing_number) : '';
      return addr.includes(term) || num.includes(term);
    });
  }, [houses, statusFilter, search]);

  const allSelectedOnPage = filtered.length > 0 && filtered.every((h) => selectedIds.has(h.id));
  const someSelectedOnPage = filtered.some((h) => selectedIds.has(h.id));

  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        filtered.forEach((h) => next.delete(h.id));
      } else {
        filtered.forEach((h) => next.add(h.id));
      }
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const performExport = async (format) => {
    setMenuOpen(false);
    setExportError(null);
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      const result = await APIAgent.exportMlsListings({
        listing_ids: Array.from(selectedIds),
        format,
      });
      setToast({
        format,
        count: selectedIds.size,
        warnings: result?.warnings || [],
        downloadUrl: result?.download_url || null,
        warningsDownloadUrl: result?.warnings_download_url || null,
        sidecarBlob: result?.warnings_blob || null,
      });
    } catch (err) {
      console.error('MLS export failed:', err);
      setExportError(err.message || 'Export failed.');
      setToast({
        format,
        count: selectedIds.size,
        error: err.message || 'Export failed.',
        warnings: [],
      });
    } finally {
      setExporting(false);
    }
  };

  // Auto-dismiss successful toasts after 6 s, but only if there are no
  // warnings (otherwise the user must dismiss manually).
  useEffect(() => {
    if (!toast) return undefined;
    if (toast.error) return undefined;
    if ((toast.warnings || []).length > 0) return undefined;
    const id = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(id);
  }, [toast]);

  if (loading) {
    return (
      <div className="admin-shell listings-manager">
        <div className="admin-loading">Loading listings…</div>
      </div>
    );
  }

  return (
    <div className="admin-shell listings-manager" data-testid="listings-manager">
      <div className="admin-header">
        <h2 className="admin-title">Listings</h2>
        <p className="admin-subtitle">
          Browse all listings. Select rows to export an MLS-compatible bundle.
        </p>
      </div>

      {error && (
        <div className="admin-banner admin-banner--error" role="alert">
          {error}{' '}
          <button
            type="button"
            className="listings-manager__retry"
            onClick={fetchHouses}
          >
            Retry
          </button>
        </div>
      )}

      <div className="listings-manager__toolbar">
        <input
          type="search"
          className="listings-manager__search"
          placeholder="Search address or #"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search listings"
          data-testid="listings-manager-search"
        />
        <select
          className="listings-manager__filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
          data-testid="listings-manager-status-filter"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="listings-manager__count">
          {filtered.length} {filtered.length === 1 ? 'listing' : 'listings'}
        </span>
      </div>

      {/* Desktop table */}
      <div className="listings-manager__table-wrap">
        <table className="listings-manager__table">
          <thead>
            <tr>
              <th scope="col">
                <input
                  type="checkbox"
                  aria-label="Select all listings on this page"
                  checked={allSelectedOnPage}
                  ref={(el) => {
                    if (el) el.indeterminate = !allSelectedOnPage && someSelectedOnPage;
                  }}
                  onChange={togglePageSelection}
                  data-testid="listings-manager-select-all"
                />
              </th>
              <th scope="col">#</th>
              <th scope="col">Address</th>
              <th scope="col">Status</th>
              <th scope="col">Updated</th>
              <th scope="col">Beds</th>
              <th scope="col">Baths</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((h) => {
              const checked = selectedIds.has(h.id);
              const hasBreakdown =
                h.full_baths != null && h.three_quarter_baths != null && h.half_baths != null;
              return (
                <tr key={h.id} className={checked ? 'listings-manager__row--selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select listing ${h.address}`}
                      checked={checked}
                      onChange={() => toggleOne(h.id)}
                      data-testid={`listings-manager-row-checkbox-${h.id}`}
                    />
                  </td>
                  <td>{h.listing_number ? `L-${h.listing_number}` : `#${h.id}`}</td>
                  <td>
                    <div className="listings-manager__addr">
                      {h.address}
                      <span className="listings-manager__addr-meta">
                        {h.city}, {h.state} {h.zip_code}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`listings-manager__badge ${statusClass(h.listing_status)}`}>
                      {h.listing_status || 'draft'}
                    </span>
                  </td>
                  <td>{formatRelativeAge(h.updated_at || h.created_at)}</td>
                  <td>{h.bedrooms ?? '—'}</td>
                  <td>
                    {h.bathrooms ?? '—'}
                    {!hasBreakdown && (
                      <span
                        className="listings-manager__legacy-marker"
                        title="Legacy listing — bath breakdown not provided. Will appear in export with full/three-quarter/half blank."
                        aria-label="Legacy bath value"
                      >
                        {' '}<span aria-hidden="true">ℹ︎</span>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="listings-manager__empty">
                  No listings match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <ul className="listings-manager__cards" aria-label="Listings (mobile)">
        {filtered.map((h) => {
          const checked = selectedIds.has(h.id);
          return (
            <li key={h.id} className={`listings-manager__card ${checked ? 'listings-manager__card--selected' : ''}`}>
              <label className="listings-manager__card-check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(h.id)}
                  aria-label={`Select listing ${h.address}`}
                />
                <div className="listings-manager__card-body">
                  <span className="listings-manager__card-num">
                    {h.listing_number ? `#L-${h.listing_number}` : `#${h.id}`}
                  </span>
                  <span className="listings-manager__card-addr">{h.address}</span>
                  <span className="listings-manager__card-meta">
                    {h.city}, {h.state} · {h.listing_status || 'draft'} · {formatRelativeAge(h.updated_at || h.created_at)}
                  </span>
                  <span className="listings-manager__card-meta">
                    {h.bedrooms ?? '—'} bd · {h.bathrooms ?? '—'} ba
                  </span>
                </div>
              </label>
            </li>
          );
        })}
      </ul>

      {/* Sticky action bar */}
      <div className="listings-manager__actions" role="region" aria-label="Bulk actions">
        <span className="listings-manager__actions-count">
          {selectedIds.size > 0
            ? `${selectedIds.size} selected`
            : 'Select at least one listing to export.'}
        </span>
        <div className="listings-manager__actions-group">
          {selectedIds.size > 0 && (
            <button
              type="button"
              className="listings-manager__btn listings-manager__btn--ghost"
              onClick={clearSelection}
            >
              Clear
            </button>
          )}
          <div className="listings-manager__menu" ref={menuRef}>
            <button
              type="button"
              ref={exportButtonRef}
              className="listings-manager__btn listings-manager__btn--primary"
              onClick={() => setMenuOpen((v) => !v)}
              disabled={selectedIds.size === 0 || exporting}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              data-testid="listings-manager-export-btn"
            >
              {exporting ? `Exporting ${selectedIds.size}…` : 'Export to MLS ▾'}
            </button>
            {menuOpen && (
              <ul className="listings-manager__menu-list" role="menu">
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="listings-manager__menu-item"
                    onClick={() => performExport('json')}
                    data-testid="listings-manager-export-json"
                  >
                    Download JSON
                  </button>
                </li>
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="listings-manager__menu-item"
                    onClick={() => performExport('csv')}
                    data-testid="listings-manager-export-csv"
                  >
                    Download CSV
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {exportError && (
        <div className="admin-banner admin-banner--error" role="alert" data-testid="listings-manager-export-error">
          {exportError}
        </div>
      )}

      {toast && <ExportToast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

function ExportToast({ toast, onClose }) {
  const warnings = toast.warnings || [];
  const isError = Boolean(toast.error);
  const hasWarnings = warnings.length > 0;
  const downloadButtonRef = useRef(null);

  // Move focus to the warnings download button when there are warnings, so
  // keyboard users find it immediately (design §5.6).
  useEffect(() => {
    if (hasWarnings && downloadButtonRef.current) {
      downloadButtonRef.current.focus();
    }
  }, [hasWarnings]);

  const [showAll, setShowAll] = useState(false);
  const visibleWarnings = showAll ? warnings : warnings.slice(0, 3);

  const downloadWarnings = () => {
    if (toast.warningsDownloadUrl) {
      window.open(toast.warningsDownloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // Fall back to inlining the warnings as a JSON blob client-side.
    const blob = new Blob(
      [JSON.stringify({ warnings }, null, 2)],
      { type: 'application/json' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'export_warnings.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className={`listings-manager__toast ${isError ? 'listings-manager__toast--error' : 'listings-manager__toast--success'}`}
      role={isError ? 'alert' : 'status'}
      data-testid="listings-manager-toast"
    >
      <div className="listings-manager__toast-head">
        {isError ? (
          <strong>Export failed.</strong>
        ) : (
          <strong>
            ✓ Exported {toast.count} {toast.count === 1 ? 'listing' : 'listings'} to {String(toast.format).toUpperCase()}.
          </strong>
        )}
        <button
          type="button"
          className="listings-manager__toast-close"
          onClick={onClose}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>

      {isError && <p className="listings-manager__toast-body">{toast.error}</p>}

      {hasWarnings && (
        <div className="listings-manager__toast-warnings">
          <p className="listings-manager__toast-warnings-summary">
            {warnings.length} {warnings.length === 1 ? 'listing has' : 'listings have'} missing required MLS fields:
          </p>
          <ul className="listings-manager__toast-warnings-list">
            {visibleWarnings.map((w) => (
              <li key={w.listing_id ?? w.listing_number ?? w.id}>
                <strong>
                  {w.listing_number ? `L-${w.listing_number}` : `#${w.listing_id ?? w.id}`}
                </strong>{' '}
                — {(w.missing_required || w.missing || []).join(', ')}
              </li>
            ))}
          </ul>
          {warnings.length > 3 && !showAll && (
            <button
              type="button"
              className="listings-manager__toast-more"
              onClick={() => setShowAll(true)}
            >
              + {warnings.length - 3} more
            </button>
          )}
          <button
            type="button"
            ref={downloadButtonRef}
            className="listings-manager__toast-download"
            onClick={downloadWarnings}
            data-testid="listings-manager-warnings-download"
          >
            Download warnings (export_warnings.json)
          </button>
        </div>
      )}
    </div>
  );
}
